import { useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import Dropdownselect from "../../widgets/Dropdown";
import { FileProcessTypeEnums, AiPerformanceLevelEnums } from "../../services/document-stores.service";

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: FileList, accuracy: string, processingType: string) => void;
  isUploading?: boolean;
}

interface FormValues {
  accuracy: string;
  processingType: string;
}

const UploadModal = ({ isOpen, onClose, onUpload, isUploading = false }: UploadModalProps) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      accuracy: AiPerformanceLevelEnums.HIGH,
      processingType: FileProcessTypeEnums.AI_OCR,
    },
  });
  const [dragActive, setDragActive] = useState(false);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const values = methods.getValues();
      onUpload(e.dataTransfer.files, values.accuracy, values.processingType);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const values = methods.getValues();
      onUpload(e.target.files, values.accuracy, values.processingType);
    }
  };

  const accuracyOptions = [
    { value: AiPerformanceLevelEnums.HIGH, label: "High" },
    { value: AiPerformanceLevelEnums.MEDIUM, label: "Medium" },
    { value: AiPerformanceLevelEnums.LOW, label: "Low" },
  ];

  const processingTypeOptions = [
    { value: FileProcessTypeEnums.AI_OCR, label: "🔍 AI OCR" },
    { value: FileProcessTypeEnums.NORMAL_OCR, label: "Standard OCR" },
  ];

  return (
    <FormProvider {...methods}>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Upload File
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Accuracy Level */}
            <Dropdownselect
              name="accuracy"
              control={methods.control as any}
              options={accuracyOptions}
              label="Accuracy level for processing"
              placeholder="Select accuracy level"
              rules={{ required: "Accuracy level is required" }}
            />

            {/* File Processing Type */}
            <Dropdownselect
              name="processingType"
              control={methods.control as any}
              options={processingTypeOptions}
              label="File Processing Type"
              placeholder="Select processing type"
              rules={{ required: "Processing type is required" }}
            />

            {/* Upload Area */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                dragActive
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 bg-gray-50"
              }`}
            >
              {/* Loading Overlay */}
              {isUploading && (
                <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center rounded-lg z-10">
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-3" />
                  <p className="text-gray-700 font-medium">Uploading file...</p>
                  <p className="text-sm text-gray-500 mt-1">Please wait</p>
                </div>
              )}

              <div className="flex flex-col items-center justify-center">
                {/* Icon */}
                <div className="relative mb-4">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-orange-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </div>
                </div>

                {/* Text */}
                <p className="text-gray-700 mb-1">
                  <label
                    htmlFor="file-upload"
                    className="text-blue-600 hover:text-blue-700 cursor-pointer font-medium"
                  >
                    Click to upload
                  </label>{" "}
                  or drag and drop file here
                </p>

                {/* File Types */}
                <p className="text-sm text-gray-500 mb-1">
                  Upload PDFs, CSV, TXT, MP3, MP4, JPG, PNG, PPTX, DOCX, MD,
                  XLSX
                </p>

                {/* File Size */}
                <p className="text-sm text-gray-500">
                  Maximum upload size:{" "}
                  <span className="text-red-500 font-medium">20 MB</span> per
                  file.
                </p>

                {/* Hidden Input */}
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  className="hidden"
                  accept=".pdf,.csv,.txt,.mp3,.mp4,.jpg,.jpeg,.png,.pptx,.docx,.md,.xlsx"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default UploadModal;
